import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader } from '@hopskipdrive/react-components';
import { useAuthQuery } from '@rideiq/auth/hooks/useAuthQuery';

import { trackFullStoryEvent } from '../../../vendor/features/FullStory/FullStory.service';
import { LoginTemplate } from '../../features/Login/LoginTemplate';

const SSO_ERROR_MESSAGE = 'SSO sign-in failed. Please try again or contact your administrator.';
const SSO_SESSION_EXPIRED_MESSAGE = 'Your sign-in session timed out. Please try again.';
const SSO_ACCESS_DENIED_MESSAGE =
  'Sign-in was denied by your identity provider. Please try again or contact your administrator.';
const SSO_EMAIL_MISMATCH_MESSAGE =
  'The account you authenticated with does not match the email you originally entered. Please sign out of your SSO provider or enter the correct email.';
const SSO_SESSION_LOAD_MESSAGE = 'Sign-in succeeded but your session could not be loaded. Please try again.';

const ERROR_CODE_MESSAGES: Record<string, string> = {
  state_mismatch: SSO_SESSION_EXPIRED_MESSAGE,
  access_denied: SSO_ACCESS_DENIED_MESSAGE,
  email_mismatch: SSO_EMAIL_MISMATCH_MESSAGE,
};

function getCallbackErrorMessage(errorCode: string | null): string {
  if (errorCode && errorCode in ERROR_CODE_MESSAGES) {
    return ERROR_CODE_MESSAGES[errorCode];
  }

  return SSO_ERROR_MESSAGE;
}

const SsoCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading } = useAuthQuery();
  const status = searchParams.get('status');
  const errorCode = searchParams.get('error_code');
  const hasTrackedLoginRef = useRef(false);

  // On error (or missing status), store a toast for the Login page to display
  // and redirect immediately. The Login page reads sessionStorage.toastMessages on mount.
  useEffect(() => {
    if (status !== 'success') {
      window.sessionStorage.setItem(
        'toastMessages',
        JSON.stringify({ severity: 'error', messages: [getCallbackErrorMessage(errorCode)] })
      );
      navigate('/login', { replace: true });
    }
  }, [status, errorCode, navigate]);

  // The profile query loads the session before navigating into the app.
  // Once the profile is loaded, navigate into the app. If the fetch fails, redirect to login with an error.
  useEffect(() => {
    if (status === 'success' && !loading) {
      if (user) {
        if (!hasTrackedLoginRef.current) {
          hasTrackedLoginRef.current = true;
          trackFullStoryEvent('user_login', { login_method: 'sso', organization_type: user.accountType });
        }
        navigate('/', { replace: true });
      } else {
        window.sessionStorage.setItem(
          'toastMessages',
          JSON.stringify({ severity: 'error', messages: [SSO_SESSION_LOAD_MESSAGE] })
        );
        navigate('/login', { replace: true });
      }
    }
  }, [status, loading, user, navigate]);

  return (
    <LoginTemplate>
      <Loader data-testid="sso-callback-loader" />
    </LoginTemplate>
  );
};

export default SsoCallback;
